import { get } from "lodash";
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { PropTypes } from "prop-types";
import {
  Container,
  Divider,
  Header,
  Loader,
  Pagination
} from "semantic-ui-react";

import UnauthorisedFeature from "../../components/auth/UnauthorisedFeature";
import RedirectWithReset from "../../components/utils/RedirectWithReset";
import Breadcrumbs from "../../components/breadcrumbs/Breadcrumbs";
import ActionDropdownMenu from "../../components/actionDropdownMenu/ActionDropdownMenu";
import ResourceMarker from "../../components/marking/ResourceMarker";
import ConnectionsForm from "../../components/connections/ConnectionsForm";
import ConnectionFilters from "../../components/connections/ConnectionFilters";
import ChallengeConfirmationModal from "../../components/modals/ChallengeConfirmationModal";
import withBasicForm from "../../components/forms/withBasicForm";

import {
  getURLFilterParams,
  updateFilterSearchParams,
  updatePageSearchParams
} from "../../utils/utils";

import { AT_TYPES, RESOURCE_URI_CHUNKS } from "../../constants/entity";
import { OPERATIONS } from "../../constants/operations";
import { PROJECTS } from "../../state/actions/actionConstants";
import { ROUTES } from "../../constants/routing";

import { operationAction } from "../../state/reducers/operations/operationActions";
import { operationAvailability } from "../../utils/operations";
import {
  getProject,
  updateProjectConnections,
  getProjectConnections
} from "../../state/reducers/projects/projectsActions";

import projectSelector from "../../state/reducers/projects/projectSelector";
import connectionsSelector from "../../state/reducers/connections/connectionsSelector";
import endpointSelector from "../../state/reducers/endpoints/endpointSelector";
import { selectCopy } from "../../state/reducers/locale/copySelector";

const ConnectionsFormBasicForm = withBasicForm(ConnectionsForm);

class ProjectAllowlist extends Component {
  static propTypes = {
    copyKey: PropTypes.string.isRequired,

    project: PropTypes.object,
    connections: PropTypes.array,
    page: PropTypes.number,
    totalPages: PropTypes.number,
    isFetching: PropTypes.bool.isRequired,
    isErrored: PropTypes.bool,
    listProjectAvailability: PropTypes.object.isRequired,
    copy: PropTypes.func.isRequired,

    getProject: PropTypes.func.isRequired,
    getProjectConnections: PropTypes.func.isRequired,
    updateProjectConnections: PropTypes.func.isRequired,
    operationAction: PropTypes.func.isRequired
  };

  static defaultProps = {
    copyKey: "project_allowlist"
  };

  constructor(props) {
    super(props);

    this.state = {
      filters: null,
      view: "card",
      redirect: null,
      resetCount: 0,

      atId: `/${RESOURCE_URI_CHUNKS.PROJECTS}/${props.match.params.id}`,
      cancelSettings: false,

      showModal: false,
      modalCopyKey: "",
      modalData: {},
      modalAction: null
    };
  }

  // -- PAGE DATA MANAGEMENT -- //

  static getDerivedStateFromProps(props, state) {
    const { location } = props;

    const redirect = state.redirect === location.search ? null : state.redirect;

    return {
      ...state,
      filters: getURLFilterParams(location),
      redirect
    };
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate(prevProps) {
    // do search params change
    if (this.props.location.search !== prevProps.location.search) {
      this.getData();
    }
  }

  getData = () => {
    const {
      location,
      isFetching,
      isErrored,
      getProject,
      getProjectConnections,
      listProjectAvailability: { available }
    } = this.props;

    this.setState({ resetCount: this.state.resetCount + 1 }, () => {
      if (!isFetching && !isErrored && available) {
        getProject(this.state.atId, false, false);
        getProjectConnections(this.state.atId, location.search);
      }
    });
  };

  // -- HANDLERS & ACTIONS -- //

  onFilterChange = data => {
    const redirect = updateFilterSearchParams(
      this.props.location.search,
      data.filters
    ).toString();

    this.setState({
      view: data.view,
      filters: data.filters,
      redirect: `?${redirect}`
    });
  };

  onPageChange = (evt, { activePage }) => {
    const redirect = updatePageSearchParams(
      this.props.location.search,
      activePage
    ).toString();

    this.setState({
      redirect: `?${redirect}`
    });
  };

  onCancel = () => {
    this.setState({ cancelSettings: true });
  };

  onReset = () => {
    this.getData();
  };

  onSubmit = values => {
    const { updateProjectConnections, location } = this.props;

    const data = {
      "@id": this.state.atId,
      ...values
    };

    updateProjectConnections(data, location.search);
  };

  handleDelete = (id, type) => {
    const modalCopyKey = `projects.modals.delete_modal`;
    const modalData = { id, type };
    const modalAction = this.doDelete;

    this.setState(
      {
        modalCopyKey,
        modalData,
        modalAction
      },
      this.openModal
    );
  };

  doDelete = () => {
    const { project, operationAction } = this.props;
    operationAction(project, OPERATIONS.DELETE);
  };

  // -- MODAL -- //

  openModal = () => {
    this.setState({ showModal: true });
  };

  closeModal = confirmed => {
    const { modalAction } = this.state;

    if (confirmed === true) modalAction();

    this.setState({ showModal: false });
  };

  // -- RENDER -- //

  getBreadcrumbSections = () => {
    const { copy, project } = this.props;

    return [
      { content: copy("navigation.projects"), href: ROUTES.PROJECTS },
      {
        content: get(project, "name", "Unknown"),
        href: get(project, "@id", "")
      },
      { content: copy("navigation.project_allowlist"), active: true }
    ];
  };

  renderHeaderSet = () => {
    const { filters, view } = this.state;
    const { project, copyKey, copy } = this.props;

    return (
      <Fragment>
        {project && (
          <Fragment>
            <Breadcrumbs sections={this.getBreadcrumbSections()} />

            <ActionDropdownMenu
              onDelete={this.handleDelete}
              viewPath={project["@id"]}
              data={project}
              copy={copy("project_dropdown_menu")}
            />

            <Header as="h3" className="action-dropdown-header">
              {`${project.name} ${copy(`${copyKey}.title`)}`}
            </Header>

            <ResourceMarker
              marking={project.marking}
              className="project-marker clearfix"
            />
          </Fragment>
        )}
        <Divider className="clear-both" />
        <ConnectionFilters
          onFilterChange={this.onFilterChange}
          initialValues={{ filters, view }}
        />
        <br />
      </Fragment>
    );
  };

  render() {
    const {
      atId,
      resetCount,
      redirect,
      modalCopyKey,
      showModal,
      cancelSettings
    } = this.state;

    const {
      project,
      page,
      totalPages,
      connections,
      isFetching,
      listProjectAvailability: { permitted, enabled },
      copyKey,
      copy
    } = this.props;

    if (redirect) {
      return (
        <RedirectWithReset to={`${location.pathname}${redirect}`} push={true} />
      );
    }

    if (cancelSettings === true) return <RedirectWithReset to={atId} />;

    if (!permitted) {
      return <UnauthorisedFeature title={copy("projects.title")} />;
    }

    // early return if page isn't ready to render
    if ((!project || !connections) && isFetching) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <Loader active inline="centered" />
        </Container>
      );
    }

    // early return if no project is returned
    if (!enabled || !project || project.length < 1) {
      return (
        <Container>
          {this.renderHeaderSet()}
          <p>{copy(`${copyKey}.none_available`)}</p>
        </Container>
      );
    }

    if (project) {
      const availability = operationAvailability(
        OPERATIONS.ADD_CONNECTION,
        project.operations,
        false
      );

      if (!availability.available) {
        return <RedirectWithReset to={atId} />;
      }
    }

    return (
      <Container>
        {this.renderHeaderSet()}

        {isFetching && <Loader active inline="centered" />}

        <ConnectionsFormBasicForm
          key={`connections-form-${resetCount}`}
          data={project}
          connections={connections}
          onCancel={this.onCancel}
          onReset={this.onReset}
          onSubmit={this.onSubmit}
          className={isFetching ? "display-none" : ""}
        />

        {totalPages != undefined && totalPages > 1 && (
          <Fragment>
            <br />
            <Pagination
              activePage={page}
              totalPages={totalPages}
              firstItem={null}
              lastItem={null}
              onPageChange={this.onPageChange}
              className={"float-right clear-both"}
            />
            <Divider hidden fitted clearing />
          </Fragment>
        )}

        {showModal && (
          <ChallengeConfirmationModal
            entity={project}
            open={showModal}
            onClose={this.closeModal}
            copyKey={modalCopyKey}
          />
        )}
      </Container>
    );
  }
}

const mapStateToProps = (state, props) => ({
  project: projectSelector.selectProject()(state, props),
  connections: connectionsSelector.selectProjectConnections(state, props),
  page: state.connections.page,
  totalPages: state.connections.totalPages,
  timeReceived: `${state.connections.timeReceived}`,
  isFetching: state.projects.isFetching || state.connections.isFetching,
  isErrored: state.api.isError,
  listProjectAvailability: endpointSelector.selectOperationAvailability(state)(
    AT_TYPES.PROJECT,
    OPERATIONS.LIST
  ),
  copy: selectCopy(state)
});

const mapDispatchToProps = dispatch => ({
  getProject: (id, usage, resources) =>
    dispatch(getProject(id, usage, resources)),
  getProjectConnections: (...args) => dispatch(getProjectConnections(...args)),
  updateProjectConnections: (...args) =>
    dispatch(updateProjectConnections(...args)),
  operationAction: (entity, operationName, data, options) =>
    dispatch(operationAction(PROJECTS, entity, operationName, data, options))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectAllowlist);
